import { inject, Injectable, signal } from '@angular/core';
import { ROUTES } from '@core/constants';
import {
  IResponseHttp,
  OrderStatus,
  QuoteStatus,
  CapturedPhoto,
  WorkOrder,
  WorkOrderChecklist,
  WorkOrderTakeRequestedService,
  WorkOrderTakeResult,
  WorkOrderTechnicalReview,
} from '@core/models';
import {
  readNullableString,
  readNullableStringByKeys,
  readNumber,
  readString,
  readStringByKeys,
  toObject,
} from '@core/utils';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class WorkOrdersService {
  private readonly api = inject(ApiService);
  private readonly _currentReview = signal<WorkOrderTakeResult | null>(null);
  readonly currentReview = this._currentReview.asReadonly();

  async getWorkOrdersByStatus(status: OrderStatus): Promise<WorkOrder[]> {
    const result = await this.api.get<IResponseHttp>(
      ROUTES.OPS_WORK_ORDER_BY_STATUS(status),
    );

    if (result.status !== 'ok' || !Array.isArray(result.payload)) {
      return [];
    }

    return result.payload
      .map((order) => this.normalizeOrder(order))
      .filter((order): order is WorkOrder => !!order);
  }

  async takeWorkOrder(
    orderId: string,
    technicianId: string,
    vehiclePlate?: string,
  ): Promise<WorkOrderTakeResult> {
    const result = await this.api.post<IResponseHttp>(ROUTES.OPS_WORK_ORDER_TAKE, {
      orderId,
      technicianId,
    });

    if (result.status !== 'ok' || !result.payload) {
      throw new Error(result.msg || 'No fue posible tomar la orden.');
    }

    const mapped = this.normalizeTakeResult(result.payload);
    if (vehiclePlate && vehiclePlate.trim().length > 0) {
      mapped.vehiclePlate = vehiclePlate.trim().toUpperCase();
    }
    this._currentReview.set(mapped);
    return mapped;
  }

  async continueOrderReview(orderId: string): Promise<WorkOrderTakeResult> {
    const result = await this.api.get<IResponseHttp>(
      ROUTES.OPS_WORK_ORDER_REVIEW(orderId),
    );

    if (result.status !== 'ok' || !result.payload) {
      throw new Error(result.msg || 'No fue posible continuar con la orden.');
    }

    const mapped = this.normalizeTakeResult(result.payload);
    this._currentReview.set(mapped);
    return mapped;
  }

  setCurrentReview(review: WorkOrderTakeResult | null): void {
    this._currentReview.set(review);
  }

  private normalizeOrder(raw: unknown): WorkOrder | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const order = raw as Record<string, unknown>;
    const vehicle =
      order['vehicle'] && typeof order['vehicle'] === 'object'
        ? (order['vehicle'] as Record<string, unknown>)
        : null;

    if (!vehicle || typeof order['id'] !== 'string') {
      return null;
    }

    const technicianRaw =
      order['technician'] && typeof order['technician'] === 'object'
        ? (order['technician'] as Record<string, unknown>)
        : null;

    const technician =
      technicianRaw &&
      typeof technicianRaw['id'] === 'string' &&
      typeof technicianRaw['name'] === 'string'
        ? {
            id: technicianRaw['id'],
            name: technicianRaw['name'],
          }
        : null;
    const technicianReviewRaw =
      order['technicianReview'] && typeof order['technicianReview'] === 'object'
        ? (order['technicianReview'] as Record<string, unknown>)
        : null;

    const technicianReview =
      technicianReviewRaw &&
      typeof technicianReviewRaw['id'] === 'string' &&
      typeof technicianReviewRaw['name'] === 'string'
        ? {
            id: technicianReviewRaw['id'],
            name: technicianReviewRaw['name'],
          }
        : null;

    return {
      id: order['id'],
      vehicle: {
        plate: readString(vehicle, 'plate'),
        brand: readString(vehicle, 'brand'),
        model: readString(vehicle, 'model'),
        year: Number(readString(vehicle, 'year')),
        engineCc: Number(readString(vehicle, 'engineCc')),
      },
      entryDate: readString(order, 'entryDate'),
      status: readString(order, 'status') as OrderStatus,
      hasTechnician: Boolean(order['hasTechnician']),
      technician,
      hasTechnicianReview: Boolean(order['hasTechnicianReview']),
      technicianReview,
      km: readNumber(order, 'km'),
      customer: {
        user: readString(order['customer'], 'user'),
        phone: readString(order['customer'], 'phone'),
      },
      quote: readString(order, 'quote') as QuoteStatus,
    };
  }

  private normalizeTakeResult(payload: unknown): WorkOrderTakeResult {
    const source = toObject(payload);
    const order = toObject(source['order']);
    const customerRaw = toObject(source['customer']);
    const vehicleRaw = toObject(source['vehicle']);
    const inspectionRaw = toObject(source['inspection']);
    const requestedServicesRaw = Array.isArray(source['requestedServices'])
      ? source['requestedServices']
      : [];

    return {
      order: {
        id: readString(order, 'id'),
        vehicleId: readString(order, 'vehicleId'),
        currentKm: readNumber(order, 'currentKm'),
        status: readString(order, 'status') as OrderStatus,
        fuelLevel: this.readOptionalNumber(order, ['fuelLevel', 'gasoline', 'fuel']),
      },
      customer: readString(customerRaw, 'name')
        ? { name: readString(customerRaw, 'name') }
        : null,
      vehicle:
        readString(vehicleRaw, 'plate') || readString(vehicleRaw, 'dataVehicle')
          ? {
              plate: readString(vehicleRaw, 'plate'),
              dataVehicle: readString(vehicleRaw, 'dataVehicle'),
            }
          : null,
      receptionPhotos: this.normalizeTechnicalReviewPhotos(source['receptionPhotos']),
      inspection:
        inspectionRaw && readString(inspectionRaw, 'id')
          ? {
              id: readString(inspectionRaw, 'id'),
              notes: readNullableString(inspectionRaw, 'notes'),
              photos: this.normalizeTechnicalReviewPhotos(inspectionRaw['photos']),
            }
          : null,
      requestedServices: requestedServicesRaw.map((item) =>
        this.normalizeRequestedService(item),
      ),
      checklist: this.normalizeChecklist(source['checklist']),
      technicalReview: source['technicalReview']
        ? this.normalizeTechnicalReview(source['technicalReview'])
        : null,
    };
  }

  private normalizeTechnicalReview(raw: unknown): WorkOrderTechnicalReview {
    const source = toObject(raw);
    const itemsRaw = Array.isArray(source['items']) ? source['items'] : [];

    return {
      id: readString(source, 'id'),
      notes: readString(source, 'notes'),
      status: readString(source, 'status'),
      items: itemsRaw.map((item) => {
        const itemObj = toObject(item);
        return {
          id: readString(itemObj, 'id'),
          serviceId: readString(itemObj, 'serviceId'),
          name: readString(itemObj, 'name'),
          description: readString(itemObj, 'description'),
          diagnosis: readString(itemObj, 'diagnosis'),
        };
      }),
    };
  }

  private normalizeTechnicalReviewPhotos(raw: unknown): CapturedPhoto[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map<CapturedPhoto | null>((item) => {
        const photo = toObject(item);
        const id = readString(photo, 'id');
        const url = readString(photo, 'url');
        const type = readStringByKeys(photo, ['type', 'evidenceType']);

        if (!id || !url) {
          return null;
        }

        return {
          id,
          fileName: this.getFileNameFromUrl(url),
          previewUrl: url,
          type,
        };
      })
      .filter((photo): photo is CapturedPhoto => photo !== null);
  }

  private getFileNameFromUrl(url: string): string {
    const cleanUrl = url.split('?')[0] ?? url;
    return cleanUrl.split('/').pop() || 'technical-review-evidence';
  }

  private normalizeRequestedService(raw: unknown): WorkOrderTakeRequestedService {
    const source = toObject(raw);
    const serviceRaw = toObject(source['service']);
    const approved = this.readOptionalBoolean(source, [
      'approved',
      'isApproved',
      'accepted',
    ]);

    return {
      id: readString(source, 'id'),
      serviceCatalogId: readNullableString(source, 'serviceCatalogId'),
      description: readNullableStringByKeys(source, [
        'description',
        'notes',
        'observation',
      ]),
      approved,
      notes: readNullableStringByKeys(source, ['notes', 'description', 'observation']),
      service:
        serviceRaw && readString(serviceRaw, 'id')
          ? {
              id: readString(serviceRaw, 'id'),
              name: readString(serviceRaw, 'name'),
            }
          : null,
    };
  }

  private normalizeChecklist(raw: unknown): WorkOrderChecklist {
    if (!raw || typeof raw !== 'object') {
      return {};
    }

    const checklistSource = raw as Record<string, unknown>;
    const entries = Object.entries(checklistSource);

    return entries.reduce<WorkOrderChecklist>((acc, [category, items]) => {
      if (!Array.isArray(items)) {
        acc[category] = [];
        return acc;
      }

      acc[category] = items.map((item) => {
        const row = toObject(item);
        return {
          id: readString(row, 'id'),
          name: readString(row, 'name'),
          status: readStringByKeys(row, ['status', 'result']),
          notes: readNullableStringByKeys(row, ['notes', 'description', 'observation']),
        };
      });
      return acc;
    }, {});
  }

  private readOptionalBoolean(value: unknown, keys: string[]): boolean | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const source = value as Record<string, unknown>;
    for (const key of keys) {
      const result = source[key];
      if (typeof result === 'boolean') {
        return result;
      }
      if (typeof result === 'string') {
        const normalized = result.trim().toLowerCase();
        if (
          ['true', 'ok', 'approved', 'aprobado', 'accepted', 'si', 'yes'].includes(
            normalized,
          )
        ) {
          return true;
        }
        if (['false', 'fail', 'rejected', 'rechazado', 'no'].includes(normalized)) {
          return false;
        }
      }
    }
    return null;
  }

  private readOptionalNumber(value: unknown, keys: string[]): number | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const source = value as Record<string, unknown>;
    for (const key of keys) {
      const result = source[key];
      if (typeof result === 'number' && Number.isFinite(result)) {
        return Math.max(0, Math.min(100, result));
      }
    }
    return null;
  }
}
